import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FactoryCard from "@/components/feature/FactoryCard";
import MembershipPricingBanner from "@/components/feature/MembershipPricingBanner";
import SeoHead, { BASE_URL } from "@/components/feature/SeoHead";
import { kuwaitFactories } from "@/mocks/kuwaitFactories";
import { Factory } from "@/components/feature/FactoryCard";

type FilterType = "all" | "verified" | "dropshipping";

const KuwaitPage = () => {
  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const factories = kuwaitFactories as unknown as Factory[];

  const filtered = factories.filter((f) => {
    const matchesFilter =
      filter === "all" ||
      (filter === "verified" && f.verified) ||
      (filter === "dropshipping" && f.dropshipping);
    const matchesSearch =
      search === "" ||
      f.name.toLowerCase().includes(search.toLowerCase()) ||
      f.categories.some((c) => c.toLowerCase().includes(search.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Kuwait Verified Factories & Dropshipping | SuperMiddleEast",
      "description": "Browse verified factories in Kuwait. Sample orders, bulk orders, and dropshipping from top Kuwait manufacturers.",
      "url": `${BASE_URL}/kuwait`,
      "inLanguage": "en",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
          { "@type": "ListItem", "position": 2, "name": "Kuwait Factories", "item": `${BASE_URL}/kuwait` },
        ],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Kuwait Verified Factories",
      "description": "Top verified factories in Kuwait offering sample orders, bulk orders and dropshipping.",
      "url": `${BASE_URL}/kuwait`,
      "numberOfItems": factories.length,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Kuwait Verified Factories & Dropshipping | SuperMiddleEast"
        description="Browse verified factories in Kuwait. Sample orders, bulk orders, and dropshipping available. Container & drop shipping from top Kuwait City manufacturers."
        keywords="Kuwait factory, Kuwait manufacturer, Kuwait dropshipping, Kuwait City verified supplier, buy direct Kuwait, Kuwait wholesale"
        canonical={`${BASE_URL}/kuwait`}
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <div className="relative h-72 md:h-96 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/5a5a3398abad7fcbd92686d55cda032d.jpg"
          alt="Kuwait City"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">🇰🇼</span>
            <span className="bg-amber-500 text-white text-sm font-bold px-3 py-1 rounded-full">Kuwait Verified Factories</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Buy Direct from Kuwait
          </h1>
          <p className="text-amber-200 text-base max-w-xl">
            Sample orders, bulk orders &amp; dropshipping from Kuwait&apos;s top verified factories
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { icon: "ri-shield-check-fill", label: "Verified Factories", value: `${factories.filter((f) => f.verified).length}+`, color: "text-amber-500" },
            { icon: "ri-truck-fill", label: "Dropshipping Ready", value: `${factories.filter((f) => f.dropshipping).length}`, color: "text-emerald-500" },
            { icon: "ri-price-tag-3-line", label: "Products Listed", value: "2,520+", color: "text-amber-500" },
            { icon: "ri-global-line", label: "Warehouse Countries", value: "9+", color: "text-emerald-500" },
          ].map((stat) => (
            <div key={stat.label} className="bg-white border border-amber-100 rounded-2xl px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl flex-shrink-0">
                <i className={`${stat.icon} ${stat.color} text-lg`}></i>
              </div>
              <div>
                <div className="font-black text-gray-900 text-lg leading-none">{stat.value}</div>
                <div className="text-gray-400 text-xs mt-0.5">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex bg-white border border-amber-100 rounded-xl p-1 gap-1">
            {(["all", "verified", "dropshipping"] as FilterType[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  filter === f ? "bg-amber-500 text-white" : "text-gray-500 hover:text-amber-600"
                }`}
              >
                {f === "all" ? "All Factories" : f === "verified" ? "✓ Verified Only" : "🚚 Welcome — Container & Drop Shipping Available"}
              </button>
            ))}
          </div>
          <div className="flex-1 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="Search Kuwait factories or categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-amber-100 rounded-xl text-sm focus:outline-none focus:border-amber-400 bg-white"
            />
          </div>
          <button
            onClick={() => navigate("/register-factory")}
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-5 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-store-2-line"></i> Register Your Factory
          </button>
        </div>

        {/* Factory Cards */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-building-2-line text-4xl mb-3 block"></i>
            <p className="text-base">No factories match your search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filtered.map((factory) => (
              <FactoryCard key={factory.id} factory={factory} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-14 bg-gradient-to-r from-amber-500 to-amber-600 rounded-3xl px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white font-black text-2xl mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Are You a Kuwait Factory Owner?
            </h3>
            <p className="text-amber-100 text-sm max-w-md">
              List your products, set your prices, and start receiving sample &amp; bulk orders from global buyers today.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={() => navigate("/register-factory")}
              className="bg-white text-amber-600 font-bold px-6 py-3 rounded-xl hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-store-2-line"></i> Register Your Factory
            </button>
            <button
              onClick={() => navigate("/how-it-works")}
              className="border-2 border-white/60 text-white font-bold px-6 py-3 rounded-xl hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-question-line"></i> How It Works
            </button>
          </div>
        </div>
      </div>
      <MembershipPricingBanner />
      <Footer />
    </div>
  );
};

export default KuwaitPage;
